import type { WordleContext, GameSaveStore } from '@wordle/game-engine';

/**
 * Type sérialisable pour sauvegarder l'état du jeu
 * (conversion des Maps en objets pour JSON)
 */
export type SerializableGameState = Omit<
  WordleContext,
  'revealedCorrectLetters'
> & {
  revealedCorrectLetters: Record<number, string>;
};

const STORAGE_KEY_PREFIX = 'wordle6:current-game-save';
const LEGACY_STORAGE_KEY = 'wordle6:current-game-save'; // Ancienne clé globale (pour migration)

/**
 * Store pour sauvegarder et restaurer l'état en cours d'une partie
 * Chaque date possède sa propre sauvegarde
 */
export class LocalStorageGameSaveStore implements GameSaveStore {
  private namespace?: string;

  constructor(namespace?: string) {
    this.namespace = namespace;
  }

  private getStoragePrefix(): string {
    return this.namespace ? `${STORAGE_KEY_PREFIX}:${this.namespace}` : STORAGE_KEY_PREFIX;
  }

  private getStorageKey(dateKey: string): string {
    return `${this.getStoragePrefix()}:${dateKey}`;
  }

  /**
   * Migration : tente de migrer l'ancienne sauvegarde globale vers la nouvelle structure par date
   * S'exécute une seule fois automatiquement
   */
  private migrateLegacySave(dateKey: string): void {
    if (typeof window === 'undefined') return;
    if (this.namespace) return;

    // Vérifie s'il existe une migration déjà faite pour cette clé
    const migrationKey = `${LEGACY_STORAGE_KEY}:migrated`;
    if (window.localStorage.getItem(migrationKey)) return;

    try {
      const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!legacyRaw) return;

      const legacyState = JSON.parse(legacyRaw);
      
      // Si la sauvegarde legacy correspond à la date demandée, on la migre
      if (legacyState.dateKey === dateKey) {
        // Sauvegarde avec la nouvelle clé
        window.localStorage.setItem(this.getStorageKey(dateKey), legacyRaw);
      }

      // Marque la migration comme faite (ne pas re-migrer à chaque fois)
      window.localStorage.setItem(migrationKey, 'true');
    } catch (error) {
      // En cas d'erreur, on ignore et on continue
    }
  }

  /**
   * Sauvegarde l'état actuel de la partie pour la date spécifiée dans context.dateKey
   */
  save(context: WordleContext): void {
    if (typeof window === 'undefined') return;

    // Ne sauvegarde que si une partie est en cours (dateKey présent)
    if (!context.dateKey) return;

    // Convertit la Map en objet pour la sérialisation
    const revealedCorrectLetters: Record<number, string> = {};
    context.revealedCorrectLetters.forEach((value, key) => {
      revealedCorrectLetters[key] = value;
    });

    const serializableState: SerializableGameState = {
      ...context,
      revealedCorrectLetters,
    };

    try {
      window.localStorage.setItem(
        this.getStorageKey(context.dateKey),
        JSON.stringify(serializableState)
      );
    } catch (error) {
      console.warn('Failed to save game state:', error);
    }
  }

  /**
   * Charge l'état sauvegardé pour la date spécifiée
   */
  load(dateKey?: string): WordleContext | null {
    if (typeof window === 'undefined') return null;
    if (!dateKey) return null;

    // Tente d'abord une migration de l'ancienne sauvegarde
    this.migrateLegacySave(dateKey);

    const raw = window.localStorage.getItem(this.getStorageKey(dateKey));
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as SerializableGameState;

      // Convertit l'objet en Map
      const revealedCorrectLetters = new Map<number, string>();
      Object.entries(parsed.revealedCorrectLetters).forEach(([key, value]) => {
        revealedCorrectLetters.set(Number(key), value);
      });

      return {
        ...parsed,
        revealedCorrectLetters,
      };
    } catch (error) {
      console.warn('Failed to load game state:', error);
      return null;
    }
  }

  /**
   * Supprime la sauvegarde pour la date spécifiée
   */
  clear(dateKey?: string): void {
    if (typeof window === 'undefined') return;
    if (!dateKey) return;
    window.localStorage.removeItem(this.getStorageKey(dateKey));
  }
}